import type { BuilderState } from "../builder-state";
import {
  isSafeIdent,
  qLiteral,
  NO_CAPS,
  type ConnectionString,
  type DialectCapabilities,
} from "./shared";

export const label = "Microsoft SQL Server";
export const defaultPort = 1433;

export const caps: DialectCapabilities = {
  ...NO_CAPS,
  schemaToggle: true,
  truncatePriv: true,
  referencesAndTrigger: true,
  wrapTransaction: true,
  quoteIdentifiers: true,
  superuserToggle: true, // db_owner
  connectionLimit: true,
  validUntil: true,
};

export function generate(s: BuilderState): string {
  const out: string[] = [];
  const push = (l = "") => out.push(l);
  const c = (t: string) => s.includeComments && push(`-- ${t}`);

  const qBracket = (v: string) => `[${v.replace(/]/g, "]]")}]`;
  const ident = (v: string) => (s.quoteIdentifiers || !isSafeIdent(v) ? qBracket(v) : v);
  const db = ident(s.dbName);
  const user = ident(s.userName);
  const login = ident(s.userName);
  const schema = ident(s.schemaName);
  const pwd = s.password ? qLiteral(s.password) : qLiteral("CHANGE_ME");

  c("============================================================");
  c(`Microsoft SQL Server — ${s.mode === "create" ? "Create" : "Update"} login, user & database`);
  c("Run as a server-level admin (sysadmin / securityadmin + dbcreator).");
  c("Tested for SQL Server 2016+. For Azure SQL DB use contained users.");
  c("============================================================");
  push();

  if (s.mode === "create") {
    c("---- 1. DATABASE --------------------------------------------");
    c("CREATE DATABASE cannot run inside a user transaction.");
    push(`IF DB_ID(${qLiteral(s.dbName)}) IS NULL`);
    push(`    CREATE DATABASE ${db};`);
    push(`GO`);
    push();
  }

  if (s.mode === "create") {
    c("---- 2. SERVER LOGIN ----------------------------------------");
    c("Logins live at the server level. Skip on Azure SQL DB.");
    push(
      `IF NOT EXISTS (SELECT 1 FROM sys.server_principals WHERE name = ${qLiteral(s.userName)})`,
    );
    push(`BEGIN`);
    const opts: string[] = [`PASSWORD = ${pwd}`];
    opts.push(`DEFAULT_DATABASE = ${db}`);
    opts.push(`CHECK_POLICY = ON`);
    if (s.validUntil) opts.push(`CHECK_EXPIRATION = ON`);
    push(`    CREATE LOGIN ${login} WITH ${opts.join(", ")};`);
    push(`END`);
    push(`GO`);
    push();
  } else if (s.resetPassword && s.password) {
    push(`ALTER LOGIN ${login} WITH PASSWORD = ${pwd};`);
    push(`GO`);
    push();
  }

  push(`USE ${db};`);
  push(`GO`);
  push();
  if (s.wrapTransaction) {
    push(`BEGIN TRANSACTION;`);
    push();
  }

  c("---- 3. DATABASE USER (mapped to login) ---------------------");
  if (s.mode === "create") {
    push(
      `IF NOT EXISTS (SELECT 1 FROM sys.database_principals WHERE name = ${qLiteral(s.userName)})`,
    );
    push(`    CREATE USER ${user} FOR LOGIN ${login};`);
    push();
  }

  if (s.createSchema) {
    c("---- 4. SCHEMA ----------------------------------------------");
    push(`IF NOT EXISTS (SELECT 1 FROM sys.schemas WHERE name = ${qLiteral(s.schemaName)})`);
    push(
      `    EXEC('CREATE SCHEMA ${qBracket(s.schemaName)} AUTHORIZATION ${qBracket(s.userName)}');`,
    );
    push(`ALTER USER ${user} WITH DEFAULT_SCHEMA = ${schema};`);
    push();
  }

  c("---- 5. PRIVILEGES ------------------------------------------");
  const target = s.createSchema ? `SCHEMA::${schema}` : `SCHEMA::${ident("dbo")}`;

  if (s.mode === "update" && s.revokeBeforeGrant) {
    c("Revoke broad privileges before re-applying.");
    push(
      `REVOKE SELECT, INSERT, UPDATE, DELETE, EXECUTE, REFERENCES, ALTER, CONTROL ON ${target} FROM ${user};`,
    );
    push();
  }

  const privs: string[] = [];
  if (s.privSelect) privs.push("SELECT");
  if (s.privInsert) privs.push("INSERT");
  if (s.privUpdate) privs.push("UPDATE");
  if (s.privDelete) privs.push("DELETE");
  if (s.privReferences) privs.push("REFERENCES");
  if (s.privTrigger) privs.push("ALTER");
  if (s.privExecuteFn) privs.push("EXECUTE");
  if (s.privTruncate && !privs.includes("ALTER")) {
    c("SQL Server requires ALTER (or db_ddladmin) to TRUNCATE TABLE.");
    privs.push("ALTER");
  }

  if (privs.length) push(`GRANT ${privs.join(", ")} ON ${target} TO ${user};`);

  if (s.privCreateSchema) {
    c("Allow the user to create new objects in the database.");
    push(`GRANT CREATE TABLE, CREATE VIEW, CREATE PROCEDURE, CREATE FUNCTION TO ${user};`);
  }
  if (s.superuser) {
    c("db_owner has full control over the database. Avoid for app accounts.");
    push(`ALTER ROLE db_owner ADD MEMBER ${user};`);
  }
  push();

  if (s.connectionLimit !== -1) {
    c(`Connection limits in SQL Server are enforced via Resource Governor (${s.connectionLimit}).`);
    push();
  }

  if (s.wrapTransaction) {
    push(`COMMIT TRANSACTION;`);
    push(`GO`);
  }

  return out.join("\n");
}

export function connectionStrings(s: BuilderState): ConnectionString[] {
  const pwd = encodeURIComponent(s.password);
  const user = encodeURIComponent(s.userName);
  const dbSeg = encodeURIComponent(s.dbName);
  const auth = pwd ? `${user}:${pwd}` : user;
  return [
    {
      label: "sqlcmd / OLE DB",
      url: `Server=${s.host},${s.port};Database=${s.dbName};User Id=${s.userName};Password=${s.password};Encrypt=true;`,
    },
    {
      label: "JDBC",
      url: `jdbc:sqlserver://${s.host}:${s.port};databaseName=${dbSeg};user=${user};password=${pwd};encrypt=true;trustServerCertificate=false`,
    },
    {
      label: "SQLAlchemy (pyodbc)",
      url: `mssql+pyodbc://${auth}@${s.host}:${s.port}/${dbSeg}?driver=ODBC+Driver+18+for+SQL+Server`,
    },
    {
      label: "Node.js (mssql / tedious)",
      url: `mssql://${auth}@${s.host}:${s.port}/${dbSeg}`,
    },
  ];
}
